/**
 * WEB COPY of LetterLevelsScreen.tsx — picked by the web bundler ahead of the
 * mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Identical to the mobile screen since the Levels redesign shipped on both
 * (2026-09-07); kept as a separate file so web-only presentation changes can
 * land here without touching the phone. When LetterLevelsScreen.tsx changes,
 * re-apply the change here.
 */
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { letterToLovedOnesService } from '../../services/letterToLovedOnesInfo';
import { onboardingService } from '../../services/onboarding';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LetterLevels');

type MarkerState = 'done' | 'active' | 'later';
type ButtonKind = 'solid' | 'outline' | 'tint';

type LevelCard = {
  key: string;
  number: number;
  state: MarkerState;
  eyebrow: string;
  title: string;
  sub: string;
  chip?: string;
  price?: string;
  button?: { label: string; kind: ButtonKind; onPress: () => void; disabled?: boolean };
  caption?: string;
  link?: { label: string; onPress: () => void };
};

const C = {
  teal: '#29BFC0',
  deepTeal: '#1D5C59',
  navy: '#1C2C4F',
  copy: '#596B85',
  sheet: '#F3F6F8',
  white: '#FFFFFF',
  line: '#DCE3E8',
  laterCard: '#F8FAFB',
  laterBorder: '#E9EEF1',
  markerLater: '#E5EAEE',
  tint: '#DFF2F1',
  chipBg: '#FFF0E0',
  orange: '#FF9F5A',
} as const;

const NEWLINE = '\n';

export const LetterLevelsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { currentTier, startFreeTrial, trialDaysRemaining, testDriveAvailable } =
    useSubscription();
  const [hasLetter, setHasLetter] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [savedLetterId, setSavedLetterId] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        try {
          const letters = await letterToLovedOnesService.getAllLettersToLovedOnes();
          // Drafts don't count as "written" — only committed letters do.
          const saved = letters.filter((l) => (l.status ?? 'saved') === 'saved');
          if (!cancelled) {
            setHasLetter(saved.length > 0);
            setSavedLetterId(saved[0]?.id ?? null);
          }
        } catch (error) {
          logger.error('Failed to load letters for Levels screen:', error);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, []),
  );

  const paidTiers = ['letter', 'basic', 'plus', 'premium'];
  const hasDelivery = paidTiers.includes(currentTier);
  const hasVault = ['basic', 'plus', 'premium'].includes(currentTier);
  // Days-remaining is only reported while a trial window is live, for
  // both plain-trial AND letter-tier test-drivers (tier stays 'letter').
  const onTrial = !hasVault && (trialDaysRemaining ?? 0) > 0;
  const hasVideo = ['plus', 'premium'].includes(currentTier);
  const hasDocuments = currentTier === 'premium';

  const goExplainer = (tier: 'basic' | 'plus' | 'premium') =>
    navigation.navigate('LetterTierExplainer', { tier });
  // Letters home is the hub: every letter, edit, and "Recordings & delivery"
  // (which opens the Saved screen). Levels never jumps straight to Saved.
  const goMyLetter = () => navigation.navigate('LettersHome');
  // The "continue with what you have" exit: they're done shopping — mark
  // the letter flow complete so relaunches land on the Dashboard too.
  const exitToDashboard = () => {
    onboardingService
      .setLetterStep('complete')
      .catch((e) => logger.debug('letter-step complete failed:', e));
    navigation.navigate('Dashboard');
  };

  // Explicit opt-in trial — never automatic, once per account.
  // testDriveAvailable comes from the server keyed on trialStartDate,
  // because hasStartedOwnTrial is also set by purchases (which hid this
  // offer from paying letter customers who never test-drove).
  const canTestDrive = testDriveAvailable && !hasVault && !onTrial;

  const handleTestDrive = () => {
    if (startingTrial) return;
    Alert.alert(
      'Test drive for free',
      'Try the information vault free for 14 days. No card needed — your letters and recordings stay yours no matter what.',
      [
        { text: 'Not now', style: 'cancel' },
        {
          text: 'Start test drive',
          onPress: async () => {
            try {
              setStartingTrial(true);
              const result = await startFreeTrial();
              if (!result.success) {
                Alert.alert('Error', result.message);
                return;
              }
              onboardingService.setLetterStep('complete');
              // The existing AI onboarding is the Level-2 front door —
              // Orion walks a fresh vault user through what matters.
              navigation.reset({
                index: 0,
                routes: [
                  {
                    name: 'Auth',
                    state: { routes: [{ name: 'AIOnboardingWelcome' }] },
                  },
                ],
              });
            } catch (error) {
              logger.error('Trial start failed:', error);
              Alert.alert('Error', 'Could not start your test drive. Please try again.');
            } finally {
              setStartingTrial(false);
            }
          },
        },
      ],
    );
  };

  // ---- Level 1: letters ---------------------------------------------------
  const letterCard: LevelCard = !hasLetter
    ? {
        key: 'l1',
        number: 1,
        state: 'active',
        eyebrow: 'Level 1 · Free',
        title: 'Your letters',
        sub: 'After you write something worth keeping, make sure it lands.',
        button: { label: 'Start letter', kind: 'solid', onPress: () => navigation.navigate('LetterHook') },
      }
    : hasDelivery
      ? {
          key: 'l1',
          number: 1,
          state: 'done',
          eyebrow: 'Level 1 · Included',
          title: 'Your letters',
          sub: 'Done. Written, saved, and set to arrive.',
          link: { label: 'View my letters', onPress: goMyLetter },
        }
      : {
          key: 'l1',
          number: 1,
          state: 'done',
          eyebrow: 'Level 1 · Included',
          title: 'Your letters',
          sub: "Written and saved. Add delivery if you'd like us to make sure it lands.",
          button: {
            label: 'Add delivery · $9.99/yr',
            kind: 'outline',
            onPress: () =>
              savedLetterId && navigation.navigate('LetterPaywall', { letterId: savedLetterId }),
          },
          link: { label: 'View my letters', onPress: goMyLetter },
        };

  // ---- Level 2: the vault -------------------------------------------------
  const vaultCard: LevelCard = hasVault
    ? {
        key: 'l2',
        number: 2,
        state: 'done',
        eyebrow: 'Level 2 · Included',
        title: 'Your information vault',
        sub: 'Personal, medical, finances, property — the details your family will need.',
        link: { label: 'Open your vault', onPress: () => navigation.navigate('Dashboard') },
      }
    : onTrial
      ? {
          key: 'l2',
          number: 2,
          state: 'active',
          eyebrow: 'Level 2 · Your test drive',
          title: 'Your information vault',
          sub: 'Personal, medical, finances, property — the details your family will need.',
          chip: `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} left`,
          button: { label: 'Keep your vault', kind: 'solid', onPress: () => goExplainer('basic') },
          caption: 'Cancel anytime. Nothing is shared until you say so.',
          link: { label: 'Open your vault', onPress: () => navigation.navigate('Dashboard') },
        }
      : canTestDrive
        ? {
            key: 'l2',
            number: 2,
            state: 'active',
            eyebrow: 'Level 2 · Try it free',
            title: 'Your information vault',
            sub: 'Personal, medical, finances, property — the details your family will need.',
            price: '$24.99',
            button: {
              label: startingTrial ? 'Starting…' : 'Test drive for free',
              kind: 'solid',
              onPress: handleTestDrive,
              disabled: startingTrial,
            },
            caption: '14 days. No card needed. Your letters stay yours no matter what.',
          }
        : {
            key: 'l2',
            number: 2,
            state: 'later',
            eyebrow: 'Level 2',
            title: 'Your information vault',
            sub: 'Personal, medical, finances, property — the details your family will need.',
            price: '$24.99',
            button: { label: 'Add the vault', kind: 'tint', onPress: () => goExplainer('basic') },
          };

  // ---- Levels 3 and 4 -----------------------------------------------------
  const videoCard: LevelCard = hasVideo
    ? {
        key: 'l3',
        number: 3,
        state: 'done',
        eyebrow: 'Level 3 · Included',
        title: 'Video messages',
        sub: 'Record video messages for the people you love, alongside your vault.',
      }
    : {
        key: 'l3',
        number: 3,
        state: 'later',
        eyebrow: 'Level 3',
        title: 'Video messages',
        sub: 'Record video messages for the people you love, alongside your vault.',
        price: '$59.99',
        button: { label: 'Add videos', kind: 'tint', onPress: () => goExplainer('plus') },
      };

  const documentsCard: LevelCard = hasDocuments
    ? {
        key: 'l4',
        number: 4,
        state: 'done',
        eyebrow: 'Level 4 · Included',
        title: 'Your documents',
        sub: 'Wills, directives, deeds, policies. Encrypted, on your phone and the web.',
        link: { label: 'Open documents', onPress: () => navigation.navigate('Documents') },
      }
    : {
        key: 'l4',
        number: 4,
        state: 'later',
        eyebrow: 'Level 4',
        title: 'Your documents',
        sub: 'Wills, directives, deeds, policies. Encrypted, on your phone and the web.',
        price: '$99.99',
        button: { label: 'Add documents', kind: 'tint', onPress: () => goExplainer('premium') },
      };

  const cards: LevelCard[] = [letterCard, vaultCard, videoCard, documentsCard];
  const firstLaterIndex = cards.findIndex((c) => c.state === 'later');

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        {navigation.canGoBack() && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <Text style={styles.title}>{`There's more here${NEWLINE}when you want it`}</Text>
          <Text style={styles.subtitle}>
            {hasLetter
              ? "You've done the part most people never get to. The rest can wait for a rainy afternoon."
              : 'Start anywhere. Most people start with the letter.'}
          </Text>

          {cards.map((card, index) => {
            const isLast = index === cards.length - 1;
            const nextIsLater = cards[index + 1]?.state === 'later';
            const showDivider = index === firstLaterIndex && firstLaterIndex > 0;
            const railColor = card.state === 'later' || nextIsLater ? C.line : C.teal;
            return (
              <React.Fragment key={card.key}>
                {showDivider && (
                  <View style={styles.dividerRow}>
                    <View style={styles.railSpacer} />
                    <View style={styles.divider}>
                      <Text style={styles.dividerText}>When you're ready</Text>
                      <View style={styles.dividerLine} />
                    </View>
                  </View>
                )}
                <View style={styles.row}>
                  <View style={styles.rail}>
                    <View
                      style={[
                        styles.marker,
                        card.state === 'done' && styles.markerDone,
                        card.state === 'active' && styles.markerActive,
                        card.state === 'later' && styles.markerLater,
                      ]}
                      accessibilityElementsHidden
                    >
                      {card.state === 'done' ? (
                        <Image
                          source={require('../../assets/icons/check.png')}
                          style={styles.markerCheck}
                        />
                      ) : (
                        <Text
                          style={[
                            styles.markerText,
                            card.state === 'later' && styles.markerTextLater,
                          ]}
                        >
                          {card.number}
                        </Text>
                      )}
                    </View>
                    {!isLast && <View style={[styles.railLine, { backgroundColor: railColor }]} />}
                  </View>

                  <View
                    style={[
                      styles.card,
                      card.state === 'active' && styles.cardActive,
                      card.state === 'later' && styles.cardLater,
                    ]}
                  >
                    <View style={styles.cardTop}>
                      <Text
                        style={[
                          styles.eyebrow,
                          card.state === 'later' && styles.eyebrowLater,
                        ]}
                      >
                        {card.eyebrow}
                      </Text>
                      {card.chip && (
                        <View style={styles.chip}>
                          <View style={styles.chipDot} />
                          <Text style={styles.chipText}>{card.chip}</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.cardTitle}>{card.title}</Text>
                    <Text style={styles.cardSub}>{card.sub}</Text>

                    {card.state === 'later' && card.price && card.button ? (
                      <View style={styles.laterFooter}>
                        <Text style={styles.price}>
                          {card.price}
                          <Text style={styles.priceUnit}>/yr</Text>
                        </Text>
                        <TouchableOpacity
                          style={styles.tintButton}
                          onPress={card.button.onPress}
                          accessibilityRole="button"
                          accessibilityLabel={card.button.label}
                        >
                          <Text style={styles.tintButtonText}>{card.button.label}</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <>
                        {card.price && (
                          <Text style={[styles.price, styles.priceInline]}>
                            {card.price}
                            <Text style={styles.priceUnit}>/yr after the test drive</Text>
                          </Text>
                        )}
                        {card.button && (
                          <TouchableOpacity
                            style={[
                              card.button.kind === 'solid' && styles.solidButton,
                              card.button.kind === 'outline' && styles.outlineButton,
                              card.button.kind === 'tint' && styles.tintButton,
                              card.button.disabled && styles.buttonDisabled,
                            ]}
                            onPress={card.button.onPress}
                            disabled={card.button.disabled}
                            accessibilityRole="button"
                            accessibilityLabel={card.button.label}
                            accessibilityState={{ disabled: !!card.button.disabled }}
                          >
                            <Text
                              style={[
                                card.button.kind === 'solid' && styles.solidButtonText,
                                card.button.kind === 'outline' && styles.outlineButtonText,
                                card.button.kind === 'tint' && styles.tintButtonText,
                              ]}
                            >
                              {card.button.label}
                            </Text>
                          </TouchableOpacity>
                        )}
                        {card.caption && <Text style={styles.caption}>{card.caption}</Text>}
                        {card.link && (
                          <TouchableOpacity
                            style={styles.link}
                            onPress={card.link.onPress}
                            accessibilityRole="button"
                            accessibilityLabel={card.link.label}
                          >
                            <Text style={styles.linkText}>{card.link.label}</Text>
                          </TouchableOpacity>
                        )}
                      </>
                    )}
                  </View>
                </View>
              </React.Fragment>
            );
          })}

          {hasLetter && (
            <View style={styles.freeExit}>
              <TouchableOpacity
                onPress={exitToDashboard}
                accessibilityRole="button"
                accessibilityLabel="Continue with what you have"
                accessibilityHint="Double tap to go to your dashboard"
              >
                <Text style={styles.freeExitLink}>
                  {hasDelivery
                    ? 'Continue with what you have'
                    : 'Continue with what you have — free'}
                </Text>
              </TouchableOpacity>
              <Text style={styles.freeExitHint}>
                Your letters and recordings stay yours to keep, edit, and export.
              </Text>
            </View>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const RAIL_W = 28;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: C.teal,
  },
  header: {
    height: s(48),
    justifyContent: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
    alignSelf: 'flex-start',
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#fff',
  },
  sheet: {
    flex: 1,
    backgroundColor: C.sheet,
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(22),
    paddingHorizontal: ms(16),
  },
  title: {
    fontSize: ms(22, 0.3),
    lineHeight: ms(27, 0.3),
    fontWeight: 'bold',
    color: C.navy,
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: C.copy,
    marginBottom: ms(18),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  rail: {
    width: RAIL_W,
    alignItems: 'center',
    marginRight: ms(12),
    paddingTop: ms(14),
  },
  railSpacer: {
    width: RAIL_W,
    marginRight: ms(12),
  },
  railLine: {
    width: 2,
    flex: 1,
    marginTop: ms(4),
    marginBottom: ms(-4),
  },
  marker: {
    width: s(26),
    height: s(26),
    borderRadius: s(13),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.teal,
  },
  markerDone: {
    backgroundColor: C.deepTeal,
  },
  markerActive: {
    backgroundColor: C.teal,
  },
  markerLater: {
    backgroundColor: C.markerLater,
  },
  markerText: {
    color: C.white,
    fontSize: ms(12, 0.3),
    fontWeight: '700',
  },
  markerTextLater: {
    color: C.copy,
  },
  markerCheck: {
    width: s(13),
    height: s(13),
    tintColor: C.white,
  },
  card: {
    flex: 1,
    backgroundColor: C.white,
    borderRadius: ms(14),
    padding: ms(16),
    marginBottom: ms(12),
    borderWidth: 1,
    borderColor: C.laterBorder,
  },
  cardActive: {
    borderWidth: 2,
    borderColor: C.teal,
  },
  cardLater: {
    backgroundColor: C.laterCard,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: ms(8),
  },
  eyebrow: {
    fontSize: ms(11, 0.3),
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: C.deepTeal,
    flexShrink: 1,
  },
  eyebrowLater: {
    color: C.copy,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(6),
    backgroundColor: C.chipBg,
    borderRadius: ms(999),
    paddingVertical: ms(4),
    paddingHorizontal: ms(10),
  },
  chipDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: C.orange,
  },
  chipText: {
    fontSize: ms(11.5, 0.3),
    fontWeight: '700',
    color: C.navy,
  },
  cardTitle: {
    fontSize: ms(18, 0.3),
    lineHeight: ms(23, 0.3),
    fontWeight: '700',
    color: C.navy,
    marginTop: ms(6),
  },
  cardSub: {
    fontSize: ms(13.5, 0.3),
    lineHeight: ms(19, 0.3),
    color: C.copy,
    marginTop: ms(6),
  },
  laterFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: ms(14),
  },
  price: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: C.navy,
  },
  priceInline: {
    marginTop: ms(12),
  },
  priceUnit: {
    fontSize: ms(11, 0.3),
    fontWeight: '400',
    color: C.copy,
  },
  solidButton: {
    backgroundColor: C.deepTeal,
    borderRadius: ms(25),
    paddingVertical: ms(13),
    paddingHorizontal: ms(16),
    alignItems: 'center',
    marginTop: ms(14),
  },
  solidButtonText: {
    color: C.white,
    fontSize: ms(14, 0.3),
    fontWeight: '700',
  },
  outlineButton: {
    alignSelf: 'flex-start',
    backgroundColor: C.white,
    borderRadius: ms(25),
    borderWidth: 1,
    borderColor: '#D8DBDD',
    paddingVertical: ms(10),
    paddingHorizontal: ms(16),
    marginTop: ms(14),
  },
  outlineButtonText: {
    color: C.navy,
    fontSize: ms(13.5, 0.3),
    fontWeight: '700',
  },
  tintButton: {
    alignSelf: 'flex-start',
    backgroundColor: C.tint,
    borderRadius: ms(25),
    paddingVertical: ms(10),
    paddingHorizontal: ms(16),
  },
  tintButtonText: {
    color: C.deepTeal,
    fontSize: ms(13.5, 0.3),
    fontWeight: '700',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  caption: {
    fontSize: ms(11.5, 0.3),
    lineHeight: ms(16, 0.3),
    color: C.copy,
    textAlign: 'center',
    marginTop: ms(8),
  },
  link: {
    alignSelf: 'center',
    paddingVertical: ms(10),
    marginTop: ms(4),
  },
  linkText: {
    color: C.navy,
    fontSize: ms(13.5, 0.3),
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  dividerRow: {
    flexDirection: 'row',
    marginTop: ms(4),
    marginBottom: ms(12),
  },
  divider: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(10),
  },
  dividerText: {
    fontSize: ms(11, 0.3),
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: C.copy,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: C.line,
  },
  freeExit: {
    alignItems: 'center',
    marginTop: ms(12),
  },
  freeExitLink: {
    color: C.navy,
    fontSize: ms(14, 0.3),
    fontWeight: '700',
    textDecorationLine: 'underline',
    padding: ms(6),
  },
  freeExitHint: {
    fontSize: ms(12, 0.3),
    lineHeight: ms(17, 0.3),
    color: C.copy,
    textAlign: 'center',
    marginTop: ms(4),
  },
  bottomPadding: {
    height: s(40),
  },
});

export default LetterLevelsScreen;
